"use client";

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { getDynamicPosterUrl } from '@/utils/imageUtils';

export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  let trimmed = url.trim();
  if (!trimmed) return '';

  // 1. Upgrade HTTP to HTTPS to prevent mixed content blocking on live HTTPS site
  if (trimmed.startsWith('http://')) {
    trimmed = 'https://' + trimmed.slice(7);
  }

  // 2. Handle Data URIs
  if (trimmed.startsWith('data:')) {
    return trimmed;
  }

  // 3. Handle local static paths (/images/..., public/images/..., images/...)
  if (trimmed.startsWith('public/')) {
    return '/' + trimmed.slice(7);
  }
  if (trimmed.startsWith('/images/') || trimmed.startsWith('/icon') || trimmed.startsWith('/manifest')) {
    return trimmed;
  }
  if (trimmed.startsWith('images/')) {
    return '/' + trimmed;
  }

  // 4. Handle TMDB relative poster paths (e.g. /A53G1S1GkI3J1fJ38gB6fLpM8n.jpg or A53G1S1GkI3J1fJ38gB6fLpM8n.jpg or /w500/...)
  if (!trimmed.startsWith('https://')) {
    const isImageFile = /\.(jpg|jpeg|png|webp|svg)(\?.*)?$/i.test(trimmed);
    if (isImageFile) {
      const cleanPath = trimmed.replace(/^\/+(w\d+\/)?/, '');
      return `https://image.tmdb.org/t/p/w342/${cleanPath}`;
    }
    if (!trimmed.startsWith('/')) {
      trimmed = '/' + trimmed;
    }
  }

  // 5. Optimize oversized TMDB & YouTube poster URLs for fast mobile loading
  if (trimmed.includes('image.tmdb.org/t/p/original/')) {
    trimmed = trimmed.replace('/t/p/original/', '/t/p/w780/');
  }
  if (trimmed.includes('image.tmdb.org/t/p/w1280/')) {
    trimmed = trimmed.replace('/t/p/w1280/', '/t/p/w780/');
  }
  if (trimmed.includes('image.tmdb.org/t/p/w500/')) {
    trimmed = trimmed.replace('/t/p/w500/', '/t/p/w342/');
  }
  if (trimmed.includes('img.youtube.com/vi/') && trimmed.includes('/maxresdefault.jpg')) {
    trimmed = trimmed.replace('/maxresdefault.jpg', '/hqdefault.jpg');
  }

  return trimmed;
}

export function SafeImage({ src, fallbackSrc, alt = '', title, subtitle, ...props }) {
  const dynamicFallback = fallbackSrc ? normalizeImageUrl(fallbackSrc) : getDynamicPosterUrl(title || alt, subtitle || 'Lyricsverse');
  
  const normalizedSrc = normalizeImageUrl(src);
  const initialSrc = normalizedSrc || dynamicFallback;
  const [imgSrc, setImgSrc] = useState(initialSrc);

  useEffect(() => {
    const norm = normalizeImageUrl(src);
    const validSrc = norm || dynamicFallback;
    setImgSrc(validSrc);
  }, [src, dynamicFallback]);

  return (
    <Image
      {...props}
      src={imgSrc}
      alt={alt || 'Movie Poster'}
      onError={() => {
        if (imgSrc !== dynamicFallback) {
          setImgSrc(dynamicFallback);
        } else {
          // Absolute fail-safe fallback
          setImgSrc(getDynamicPosterUrl(title || alt || 'Lyricsverse', 'Music'));
        }
      }}
    />
  );
}
